<template>
  <div class="disclosure">
    <h1>2024 EV9 Charge Plan</h1>
    <h2>Promotion Terms and Conditions</h2>
    <p class="primary-color"><strong>Effective as of December 2023</strong></p>
    <p>
      Electrify America and Kia America are pleased to provide this promotional charging plan ("Plan") to drivers of new
      model year 2024 Kia EV9 vehicles purchased from an authorized Kia retailer on or before December 31, 2025. By
      clicking “Agree” below, you agree to participate in this promotion, subject to your compliance with these terms
      and conditions ("Plan Terms").
    </p>
    <p>
      Participation requires an Electrify America account, the Electrify America mobile app, enrollment in the 2024 EV9
      Charging Plan on or before December 31, 2025, and acceptance of the Electrify America
      <router-link class="link" :to="{ name: 'terms-en' }">Terms of Use</router-link> and
      <router-link class="link" :to="{ name: 'privacy-en' }">Privacy Policy</router-link>. Your mobile carrier’s
      messaging, data, and other rates and fees may apply to your use of the Electrify America app. You may also be
      required to use a credit or debit card to pre-fund dollars to your account for idle fees or other charging
      sessions not covered by your plan and agree to an automatic reload feature for such purposes. See the Electrify
      America Terms of Use available at
      <router-link class="link" :to="{ name: 'terms-en' }">https://www.electrifyamerica.com/terms</router-link> for
      further details.
    </p>
    <p>
      Immediately upon your registration and acceptance of these Plan Terms, you will be enrolled in the Plan for 1000
      kWh of DC fast (CCS) charging for your EV9 on Electrify America’s network of electric vehicle charging stations.
      This Plan continues for a 36-month period starting from the date of vehicle purchase (provided to Electrify
      America by your vehicle’s manufacturer) until the benefit is depleted or unless earlier terminated by Electrify
      America as described below.
    </p>
    <p>
      After enrollment in this Plan, you can redeem your 1000 kWh of charging under this Plan at any Electrify America
      charging station, <strong>exclusively</strong> by using the Electrify America app "Tap to start" or NFC Pass
      feature with the 2024 EV9 Charge Plan selected.
      <strong
        >The charging included under this Plan may not be initiated using the interface screen or buttons on the
        charger.</strong
      >
    </p>
    <p>
      Other than pre-funding your account per our
      <router-link class="link" :to="{ name: 'terms-en' }">Terms of Use</router-link>, there is no additional fee,
      purchase, payment, or subscription required for your access to this promotion. Subject to the waiting period and
      other restrictions described below, there is no limit on the number of eligible charging sessions or the kWh
      amount of charging available in each session during the Plan.
      <strong
        >Upon depletion or expiration of this benefit, charging will continue uninterrupted at the prevailing Electrify
        America Pass charging price.</strong
      >
    </p>
    <p class="primary-color">
      <strong>PLEASE NOTE THE FOLLOWING IMPORTANT LIMITATIONS ON YOUR USE OF THIS PROMOTION:</strong>
    </p>
    <p>
      You may <strong>only</strong> use this complimentary credit for charging the EV9 whose VIN you submitted when
      signing up for this promotion on your Electrify America account.
      <strong>No other vehicle is eligible to receive this credit through your account</strong>, whether owned by you or
      others. You may not use this promotion benefit for commercial purposes, including but not limited to fleet use or
      use in a taxi or ride share program.
    </p>
    <p>
      Complimentary charging time <strong>does not include </strong> any idle fees or applicable taxes assessed at the
      charging station. You will be responsible for paying such fees through your account if you incur them during a
      charging session.
    </p>
    <p>
      You must follow all product, vehicle, safety, and technical documentation included with the EV9 when charging at
      an Electrify America charging station.
    </p>
    <p>You must use the correct connector type for the EV9 at the charging station.</p>
    <p>
      Once the 1000 kWh of included charging is depleted, you will not be able to reload, renew, or add more credit to
      the Plan. Please refer to the information on the
      <strong class="primary-color">"Plan Details"</strong> section of the app to keep track of date of expiration,
      which is 36 months (3 years) from your date of purchase.
    </p>
    <p>
      This credit is not returnable or redeemable for any cash value, and may not be sold, loaned, or otherwise
      distributed to any third party for any reason. You may not transfer any portion of the credit to any other
      Electrify America account. This credit is for your personal use only, and the plan is not authorized, intended, or
      available for any commercial use (such as ride-hailing and ridesharing).
    </p>
    <p>
      You acknowledge that your use of Electrify America’s services is subject to the Electrify America Terms of Use
      available at
      <router-link class="link" :to="{ name: 'terms-en' }">https://www.electrifyamerica.com/terms</router-link> and that
      your use of the Electrify America mobile app is subject to the Terms of Use accessible by navigating to the
      <strong class="primary-color">Profile </strong> icon in top right hand corner of map, selecting
      <strong class="primary-color">"Legal"</strong> and then selecting
      <router-link class="medium" :to="{ name: 'terms-en' }">"Terms of Use"</router-link> &amp;
      <router-link class="medium" :to="{ name: 'privacy-en' }">"Privacy Policy."</router-link>
      Please read this Terms of Use document carefully for information concerning payment through your account for idle
      fees and applicable taxes, as well as charging sessions outside of the complimentary charging offered under this
      promotion.
    </p>
    <p>
      You agree to comply with all applicable laws and regulations when using Electrify America services and when
      charging your vehicle at Electrify America charging stations.
    </p>
    <p>
      Electrify America reserves the right to withhold, revoke, reduce, terminate, or suspend your access to all or any
      portion of this promotion, without notice, if Electrify America determines or suspects, in its sole discretion,
      that you: (a) are in violation of the Electrify America Terms of Use, these Promotion Terms and Conditions, or any
      other contract between you and Electrify America; (b) have engaged in charging sessions that are excessive,
      fraudulent, or otherwise indicate an unauthorized or unintended use of this complimentary promotion; or (c) have
      engaged in any illegal, fraudulent, tortious, injurious, harmful, or abusive conduct in your use of the Electrify
      America services, the Electrify America mobile app, or an Electrify America charging station.
    </p>
    <p>
      Electrify America reserves the right to end this complimentary promotion at any time before its automatic
      termination date for any reason, in its sole discretion, provided that Electrify America will send an email to the
      address associated with your account in the event of any change to the scheduled end date of this promotion.
      Following the termination or expiration of this program for any reason, your access to the complimentary promotion
      will immediately cease, and you may not use or redeem any additional complimentary charging time. At that time,
      your account will automatically convert to a basic Electrify America “Pass” account, at no additional fee or
      charge to you (neither one-time nor recurring). You may, but are not obligated to, sign up for a different type of
      subscription via the Electrify America mobile app.
    </p>
  </div>
</template>

<script>
export default {
  name: '2024-ev9-charge-plan-disclosure',
  metaInfo: {
    title: '2024 Kia EV9 Charging Program Disclosure | Electrify America',
    link: [{ rel: 'canonical', href: 'https://www.electrifyamerica.com/2024-ev9-charge-plan-disclosure/' }],
  },
};
</script>
